import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { toast } from "react-toastify";
import { apiputFormData, apigetPublic } from "../../service/api";

const StateEditModal = ({ open, onClose, stateData, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    additionalOptions: [""],
    branches: [],
    imageUrl: ""
  });

  const [stateImageFile, setStateImageFile] = useState(null);
  const [branchesList, setBranchesList] = useState([]);

  // ------------------------------------------------
  // Load initial form data when modal opens
  // ------------------------------------------------
  useEffect(() => {
  if (stateData) {
    setFormData({
      name: stateData.name || "",
      code: stateData.code || "",
      additionalOptions: stateData.additionalOptions?.length
        ? stateData.additionalOptions
        : [""],
      branches: stateData.branches?.map((b) => b._id) || [],
      imageUrl: stateData.imageUrl || "",
    });
  }
}, [stateData]);


  // ------------------------------------------------
  // Load all branches for checkbox list
  // ------------------------------------------------
  useEffect(() => {
    const loadBranches = async () => {
      const res = await apigetPublic("branches/search");
      if (res?.status === 200) setBranchesList(res.data.result);
    };
    loadBranches();
  }, []);

  if (!stateData) return null;

  // ------------------------------------------------
  // Change handlers
  // ------------------------------------------------
  const handleFieldChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleChangeOption = (index, value) => {
    const updated = [...formData.additionalOptions];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, additionalOptions: updated }));
  };

  const handleAddOption = () => {
    setFormData((prev) => ({
      ...prev,
      additionalOptions: [...prev.additionalOptions, ""],
    }));
  };

  const handleRemoveOption = (index) => {
    const updated = formData.additionalOptions.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, additionalOptions: updated }));
  };

  const handleBranchCheckboxChange = (branchId, checked) => {
  let updated = [...formData.branches];

  if (checked) updated.push(branchId);
  else updated = updated.filter((id) => id !== branchId);

  setFormData((prev) => ({ ...prev, branches: updated }));
};

  // ------------------------------------------------
  // Submit PUT request
  // ------------------------------------------------
  const handleSubmit = async () => {
    try {
      const data = new FormData();

      data.append("name", formData.name);
      data.append("code", formData.code.toUpperCase());

      // Additional Options
      formData.additionalOptions
        .filter((opt) => opt.trim() !== "")
        .forEach((opt, idx) => {
          data.append(`additionalOptions[${idx}]`, opt);
        });

      // Branches
      formData.branches.forEach((branch, idx) => {
        data.append(`branches[${idx}]`, branch);
      });

      // Upload image if changed
      if (stateImageFile) {
        data.append("branchImage", stateImageFile);
      }

      const result = await apiputFormData(`states/${stateData._id}`, data);

      if (result?.status === 200) {
        toast.success("State updated successfully!");
        onUpdate();
        onClose();
      } else {
        toast.error("Failed to update state.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error while updating the state.");
    }
  };

  // ------------------------------------------------
  // Render UI
  // ------------------------------------------------
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit State</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="name"
              label="State Name"
              value={formData.name}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="code"
              label="State Code (ex: FL)"
              value={formData.code}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }))
              }
            />
          </Grid>

          {/* Current Image */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Current Image
            </Typography>

            {formData.imageUrl && (
              <img
                src={formData.imageUrl}
                alt="State"
                style={{
                  width: 150,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              />
            )}

            <Typography variant="subtitle2">Upload New Image</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setStateImageFile(e.target.files[0])}
            />
          </Grid>

          {/* Additional Options */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Additional Options
            </Typography>

            {formData.additionalOptions.map((opt, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label={`Option #${index + 1}`}
                  value={opt}
                  onChange={(e) => handleChangeOption(index, e.target.value)}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveOption(index)}
                  disabled={formData.additionalOptions.length === 1}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}

            <Button startIcon={<AddIcon />} onClick={handleAddOption}>
              Add Another Option
            </Button>
          </Grid>

          {/* Branch List */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Branches Assigned to This State
            </Typography>

          {branchesList.map((branch) => {
  const branchId = branch._id;
  const checked = formData.branches.includes(branchId);
  const checkboxId = `branch-${branchId}`;

  return (
    <Box key={branchId} sx={{ mb: 1 }}>
      <input
        id={checkboxId}
        type="checkbox"
        checked={checked}
        onChange={(e) =>
          handleBranchCheckboxChange(branchId, e.target.checked)
        }
      />
      <label htmlFor={checkboxId} style={{ marginLeft: 8, cursor: "pointer" }}>
        {branch.name} — {branch.city}, {branch.stateCode}
      </label>
    </Box>
  );
})}


          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StateEditModal;
